import React, { useState, useMemo, useRef } from 'react';
import {
  Button,
  Col,
  Row,
  Box,
  VerticalSpacing,
  Text,
  RadioGroup,
  Expando,
  Input,
} from '../../components/Boneyard';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import ExpandoLabel from './ExpandoLabel';
import InputField from '../../components/InputField';
import { Formik, Form } from 'formik';
import Select from '@g4/next-furniture/FlightSearchForm/flightSearchForm/Select/Select';
import FOREIGN_AGENCIES_ICON from '../../images/ic_enrollment-all-agencies.jpg';
import US_AGENCIES_ICON from '../../images/ic_enrollment-us-agencies.jpg';
import ENROLLMENT_PAGE_TRANSLATIONS_QUERY from './taLoginPageTranslations.query.graphql.js';
import COUNTRIES_QUERY from './countries.query.graphql.js';
import {
  withApplicationQuery,
  useTranslations,
} from '../../graphql/components/ApplicationQueryProvider';
import { useSSRQuery } from '../../graphql';
import FormField from '../../components/FormField';
import FieldErrorMessage from '../../components/FieldErrorMessage';
import Markdown from '../../components/Markdown';
import {
  AGENCY_NAME,
  AGENCY_ADDRESS,
  SELECT_COUNTRY,
  SELECT_STATE,
  AGENCY_PHONE_NUMBER,
  AGENCY_FAX_NUMBER,
  AGENCY_EMAIL_ADDRESS,
  AGENCY_CONTACT_NAME,
  AGENCY_IDENTIFICATION_NUMBER,
  AGENCY_IDENTIFICATION_TYPE,
  CREATE_AGENCY_PASSWORD,
  AGENCY_CERTIFICATE_UPLOAD,
  W_CERTIFICATE_UPLOAD,
  COMMISION_AGENCY_NAME,
  COMMISION_AGENCY_ADDRESS,
  COMMISION_AGENCY_COUNTRY,
  COMMISION_AGENCY_STATE,
  COMMISION_AGENCY_CITY,
  COMMISION_AGENCY_PINCODE,
  PIN_CODE,
  AGENCY_CITY,
} from './partsName';
import { validationSchema } from '../../utils/validateLoginPage';
import useStatesValues from './useStatesValues';
import ALERTICON from '../../images/ic-alert-blue-outline.svg';

const StyledBox = styled(Box)`
  display: flex;
  font-size: 12px;
  align-items: baseline;
  justify-content: space-between;
  background-color: ${props => props?.theme.colors.common.taBackground};
`;

const AgentDetails = styled(Box)`
  width: 350px;
  height: 400px;
  padding: 20px;
`;

const StyledAgencyContent = styled(Text)`
  line-height: 25px;
  font-size: 13px;
`;

const StyledAgencyHeading = styled(Text)`
  padding: 15px;
  font-size: 16px;
  line-height: 3px;
  font-weight: bold;
`;

const AgencyImg = styled.img.attrs({
  alt: '',
})`
  width: 75px;
  height: 75px;
  margin-top: 50px;
  margin-bottom: 50px;
  float: right;
`;

const StyledSection = styled(Box)`
  font-size: 13px;
  line-height: 30px;
  background-color: ${props => props?.theme.colors.common.light};
`;

const StyledButton = styled(Button)`
  width: 180px;
  margin: 25px;
  float: right;
`;

const StyledContentBox = styled(Box)`
  padding: 20px;
  background-color: ${props => props?.theme.colors.common.light};
`;

const StyledTitle = styled(Text)`
  font-size: 25px;
  font-weight: bold;
`;

const StyledText = styled(Text)`
  padding: 0px 20px;
`;

const StyledImg = styled.img`
  height: 20px;
`;

const propTypes = {
  theme: PropTypes.object,
};

const TAEnrollmentPage = ({ theme }) => {
  const {
    travelAgentEnrollmentPage: {
      taEnrollmentTitleLabel,
      taEnrollmentAllAgenciesLabel,
      taEnrollmentAllAgenciesContent,
      taEnrollmentForeignAgenciesLabel,
      taEnrollmentForeignAgenciesContent,
      taEnrollmentAmericanAgenciesLabel,
      taEnrollmentAmericanAgenciesContent,
      taFormContentLabel,
      taAgencyNameLabel,
      taAgencyAddressLabel,
      taSelectCountryLabel,
      taSelectStateLabel,
      taPhoneNumberLabel,
      taFaxNumberLabel,
      taEmailAddressLabel,
      taContactNameLabel,
      taIdentificationNumberLabel,
      taRadioTrueOption,
      taRadioArcOption,
      taRadioCliaOption,
      taRadioIataOption,
      taRadioNumberContent,
      taCreateAgencyPasswordLabel,
      taCreateAgencyPasswordContent,
      taAgencyCertificateLabel,
      taW9orW8BenLabel,
      taCorporateOfficeContent,
      taSubmitButtonLabel,
      taCancelButtonLabel,
      taSelectStatePlaceHolderLabel,
      taAgencyAddressPlaceHolderLabel,
      taCityPlaceHolderLabel,
      taPincodePlaceHolderLabel,
      taSelectCountryPlaceHolderLabel,
      taEnrollmentSuccessfulMessage,
    },
    travelAgentErrorMessage,
  } = useTranslations();

  const { data } = useSSRQuery(COUNTRIES_QUERY);

  const countries = data?.countries;

  const countriesOptions = useMemo(() => {
    return countries?.map(c => ({
      label: c.name,
      value: c.code,
    }));
  }, [countries]);

  const agencyContents = [
    {
      title: taEnrollmentAllAgenciesLabel,
      content: taEnrollmentAllAgenciesContent,
    },
    {
      title: taEnrollmentForeignAgenciesLabel,
      content: taEnrollmentForeignAgenciesContent,
      image: FOREIGN_AGENCIES_ICON,
    },
    {
      title: taEnrollmentAmericanAgenciesLabel,
      content: taEnrollmentAmericanAgenciesContent,
      image: US_AGENCIES_ICON,
    },
  ];

  const initialFormValues = {
    [AGENCY_NAME]: '',
    [AGENCY_ADDRESS]: '',
    [SELECT_COUNTRY]: '',
    [SELECT_STATE]: '',
    [AGENCY_PHONE_NUMBER]: '',
    [AGENCY_FAX_NUMBER]: '',
    [AGENCY_EMAIL_ADDRESS]: '',
    [AGENCY_CONTACT_NAME]: '',
    [AGENCY_IDENTIFICATION_TYPE]: '',
    [AGENCY_IDENTIFICATION_NUMBER]: '',
    [CREATE_AGENCY_PASSWORD]: '',
    [AGENCY_CERTIFICATE_UPLOAD]: '',
    [W_CERTIFICATE_UPLOAD]: '',
    [COMMISION_AGENCY_COUNTRY]: '',
    [COMMISION_AGENCY_STATE]: '',
    [COMMISION_AGENCY_NAME]: '',
    [COMMISION_AGENCY_ADDRESS]: '',
    [COMMISION_AGENCY_COUNTRY]: '',
    [COMMISION_AGENCY_STATE]: '',
    [COMMISION_AGENCY_CITY]: '',
    [COMMISION_AGENCY_PINCODE]: '',
    [AGENCY_CITY]: '',
    [PIN_CODE]: '',
  };

  const [isExpanded, setIsExpanded] = useState(false);

  const [details, setDetails] = useState(initialFormValues);

  const handleCountryChanges = values => {
    setDetails(values);
  };

  const stateOptions = useStatesValues(details[SELECT_COUNTRY], countries);
  const [showSuccessMessage, setSuccessMessage] = useState(false);
  const stateOptionsCommission = useStatesValues(
    details[COMMISION_AGENCY_COUNTRY],
    countries
  );

  const agentCertificateRef = useRef(null);
  const wCertificateRef = useRef(null);

  const [invalidFileSize, setInvalidFileSize] = useState({
    [AGENCY_CERTIFICATE_UPLOAD]: false,
    [W_CERTIFICATE_UPLOAD]: false,
  });

  const toggleSelected = () => {
    setIsExpanded(!isExpanded);
  };

  const handleFileChange = file => {
    //we'll explore this approach after code freeze "https://stackoverflow.com/questions/54020719/validating-file-size-and-format-with-yup"

    if (file) {
      const SIZE_LIMIT = 2048000;

      setInvalidFileSize({
        ...invalidFileSize,
        [file.name]: file.files[0]?.size > SIZE_LIMIT,
      });

      if (file.files[0]?.size > SIZE_LIMIT) {
        file.value = '';
      }
    }
  };

  const submitHandler = (values, { setFieldError, resetForm }) => {
    const inputFiles =
      typeof window !== 'undefined'
        ? document.querySelectorAll('input[type="file"]')
        : null;
    const formData = new FormData();

    formData.append(
      'enrollAgentDetails',
      JSON.stringify({
        ...values,
        agencycountry: values.agencycountry.label,
        commisionagencycountry: values.commisionagencycountry.label,
        commisionagencystate: values.commisionagencystate.label,
        agencystate: values.agencystate.label,
      })
    );
    if (inputFiles) {
      for (const file of inputFiles) {
        formData.append(file.name, file.files[0]);
      }
    }

    fetch('/travelagentenroll', {
      method: 'POST',
      body: formData,
    })
      .then(res => {
        if (res.ok) {
          resetForm();
          setSuccessMessage(prevState => !prevState);
        }
      })
      .catch(err => {
        err.length &&
          setFieldError(
            AGENCY_NAME,
            travelAgentErrorMessage.unknownLoginMessage
          );
      });
  };

  return (
    <>
      {showSuccessMessage && (
        <Box
          d="flex"
          p={2}
          justifyContent="space-between"
          backgroundColor={theme.colors.common.taEnrollmentSuccessBackGround}
          h={60}
        >
          <StyledImg src={ALERTICON} alt="" />
          <Text weight={750} size={15} color={theme.colors.common.peacockBlue}>
            {taEnrollmentSuccessfulMessage}
          </Text>
        </Box>
      )}

      <StyledSection>
        <StyledContentBox>
          <Box>
            <StyledTitle>{taEnrollmentTitleLabel}</StyledTitle>
          </Box>
        </StyledContentBox>
        <VerticalSpacing size={3} />

        <StyledText>
          <Markdown source={taFormContentLabel} />
        </StyledText>

        <Expando
          label={
            <ExpandoLabel
              isExpanded={isExpanded}
              toggleSelected={toggleSelected}
            />
          }
          open={isExpanded}
          icon="NONE"
          a11yLabel="Toggle content"
        >
          <StyledBox>
            {agencyContents.map(agency => (
              <AgentDetails>
                <StyledAgencyHeading>{agency.title}</StyledAgencyHeading>
                <StyledAgencyContent>
                  {agency.image && <AgencyImg src={agency.image} />}
                  <Markdown source={agency.content} />
                </StyledAgencyContent>
              </AgentDetails>
            ))}
          </StyledBox>
        </Expando>
        <VerticalSpacing size={3} />

        <StyledContentBox>
          <Formik
            initialValues={initialFormValues}
            validationSchema={validationSchema(travelAgentErrorMessage)}
            validateOnChange={true}
            onSubmit={submitHandler}
          >
            {({ values, touched, handleChange, errors, handleReset }) => {
              return (
                <Form onChange={handleChange} encType="multipart/form-data">
                  <VerticalSpacing size={3} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_NAME}
                          type="text"
                          label={taAgencyNameLabel}
                          value={values[AGENCY_NAME]}
                          hasError={errors[AGENCY_NAME] && touched[AGENCY_NAME]}
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_NAME} />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_ADDRESS}
                          type="text"
                          label={taAgencyAddressLabel}
                          placeholder={taAgencyAddressPlaceHolderLabel}
                          value={values[AGENCY_ADDRESS]}
                          hasError={
                            errors[AGENCY_ADDRESS] && touched[AGENCY_ADDRESS]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_ADDRESS} />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%">
                        <Text>{taSelectCountryLabel}</Text>
                        <FormField
                          component={Select}
                          name={SELECT_COUNTRY}
                          isSearchable={true}
                          placeholder={taSelectCountryPlaceHolderLabel}
                          options={countriesOptions}
                          hasError={
                            errors[SELECT_COUNTRY] && touched[SELECT_COUNTRY]
                          }
                          value={values[SELECT_COUNTRY]}
                          isRequired
                          onChange={handleCountryChanges(values)}
                        />
                        <FieldErrorMessage fieldName={SELECT_COUNTRY} />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%">
                        <Text>{taSelectStateLabel}</Text>
                        <FormField
                          component={Select}
                          name={SELECT_STATE}
                          placeholder={taSelectStatePlaceHolderLabel}
                          isSearchable={true}
                          options={stateOptions}
                          hasError={
                            errors[SELECT_STATE] && touched[SELECT_STATE]
                          }
                          value={values[SELECT_STATE]}
                          isRequired
                        />
                        <FieldErrorMessage fieldName={SELECT_STATE} />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          placeholder={taCityPlaceHolderLabel}
                          name={AGENCY_CITY}
                          value={values[AGENCY_CITY]}
                          hasError={errors[AGENCY_CITY] && touched[AGENCY_CITY]}
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_CITY} />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          placeholder={taPincodePlaceHolderLabel}
                          name={PIN_CODE}
                          value={values[PIN_CODE]}
                          hasError={errors[PIN_CODE] && touched[PIN_CODE]}
                          isRequired
                        />
                        <FieldErrorMessage fieldName={PIN_CODE} />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_PHONE_NUMBER}
                          type="text"
                          label={taPhoneNumberLabel}
                          value={values[AGENCY_PHONE_NUMBER]}
                          hasError={
                            errors[AGENCY_PHONE_NUMBER] &&
                            touched[AGENCY_PHONE_NUMBER]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_PHONE_NUMBER} />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_FAX_NUMBER}
                          type="text"
                          label={taFaxNumberLabel}
                          value={values[AGENCY_FAX_NUMBER]}
                          hasError={
                            errors[AGENCY_FAX_NUMBER] &&
                            touched[AGENCY_FAX_NUMBER]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_FAX_NUMBER} />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_EMAIL_ADDRESS}
                          type="text"
                          label={taEmailAddressLabel}
                          value={values[AGENCY_EMAIL_ADDRESS]}
                          hasError={
                            errors[AGENCY_EMAIL_ADDRESS] &&
                            touched[AGENCY_EMAIL_ADDRESS]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_EMAIL_ADDRESS} />
                      </Box>
                    </Col>
                    <VerticalSpacing size={1} />
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={AGENCY_CONTACT_NAME}
                          type="text"
                          label={taContactNameLabel}
                          value={values[AGENCY_CONTACT_NAME]}
                          hasError={
                            errors[AGENCY_CONTACT_NAME] &&
                            touched[AGENCY_CONTACT_NAME]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={AGENCY_CONTACT_NAME} />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%">
                        <Text>{taIdentificationNumberLabel}</Text>
                        <FormField
                          component={RadioGroup}
                          layout="vertical"
                          name={AGENCY_IDENTIFICATION_TYPE}
                          type="text"
                          value={values[AGENCY_IDENTIFICATION_TYPE]}
                          options={[
                            {
                              value: 'TRUE',
                              label: <Text>{taRadioTrueOption}</Text>,
                            },
                            {
                              value: 'ARC',
                              label: <Text>{taRadioArcOption}</Text>,
                            },
                            {
                              value: 'CLIA',
                              label: <Text>{taRadioCliaOption}</Text>,
                            },
                            {
                              value: 'IATA',
                              label: <Text>{taRadioIataOption}</Text>,
                            },
                          ]}
                          isRequired
                        />
                        <VerticalSpacing size={1} />
                        <InputField
                          name={AGENCY_IDENTIFICATION_NUMBER}
                          value={values[AGENCY_IDENTIFICATION_NUMBER]}
                          hasError={
                            errors[AGENCY_IDENTIFICATION_NUMBER] &&
                            touched[AGENCY_IDENTIFICATION_NUMBER]
                          }
                        />
                        <FieldErrorMessage
                          fieldName={AGENCY_IDENTIFICATION_NUMBER}
                        />

                        <VerticalSpacing size={1} />
                        <Text size={11} weight={500}>
                          {taRadioNumberContent}
                        </Text>
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%">
                        <FormField
                          component={Input}
                          name={CREATE_AGENCY_PASSWORD}
                          type="password"
                          label={taCreateAgencyPasswordLabel}
                          value={values[CREATE_AGENCY_PASSWORD]}
                          hasError={
                            errors[CREATE_AGENCY_PASSWORD] &&
                            touched[CREATE_AGENCY_PASSWORD]
                          }
                          isRequired
                        />
                        <FieldErrorMessage fieldName={CREATE_AGENCY_PASSWORD} />
                      </Box>
                    </Col>
                    <Text size={11} weight={500}>
                      {taCreateAgencyPasswordContent}
                    </Text>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={6}>
                      <Box pb={5} pt={6}>
                        <InputField
                          inputRef={agentCertificateRef}
                          label={taAgencyCertificateLabel}
                          type="file"
                          name={AGENCY_CERTIFICATE_UPLOAD}
                          value={values[AGENCY_CERTIFICATE_UPLOAD]}
                          withBorder={false}
                          onChange={() =>
                            handleFileChange(agentCertificateRef.current)
                          }
                          isRequired
                        />
                        <FieldErrorMessage
                          errorMessage={
                            invalidFileSize[AGENCY_CERTIFICATE_UPLOAD] &&
                            travelAgentErrorMessage.agencyFileUploadLimitMessage
                          }
                          fieldName={AGENCY_CERTIFICATE_UPLOAD}
                        />
                      </Box>
                    </Col>
                    <Col span={6}>
                      <Box pb={5} pt={6}>
                        <InputField
                          inputRef={wCertificateRef}
                          label={taW9orW8BenLabel}
                          type="file"
                          name={W_CERTIFICATE_UPLOAD}
                          value={values[W_CERTIFICATE_UPLOAD]}
                          withBorder={false}
                          onChange={() =>
                            handleFileChange(wCertificateRef.current)
                          }
                          isRequired
                        />
                        <FieldErrorMessage
                          errorMessage={
                            invalidFileSize[W_CERTIFICATE_UPLOAD] &&
                            travelAgentErrorMessage.agencyFileUploadLimitMessage
                          }
                          fieldName={W_CERTIFICATE_UPLOAD}
                        />
                      </Box>
                    </Col>
                    <VerticalSpacing size={5} />
                  </Row>
                  <VerticalSpacing size={1} />

                  <Row>
                    <Text size={15}>{taCorporateOfficeContent}</Text>
                    <VerticalSpacing size={4} />
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          label={taAgencyNameLabel}
                          type="text"
                          name={COMMISION_AGENCY_NAME}
                          value={values[COMMISION_AGENCY_NAME]}
                        />
                      </Box>
                    </Col>
                    <VerticalSpacing size={2} />
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          label={taAgencyAddressLabel}
                          type="text"
                          name={COMMISION_AGENCY_ADDRESS}
                          value={values[COMMISION_AGENCY_ADDRESS]}
                          placeholder={taAgencyAddressPlaceHolderLabel}
                        />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <FormField
                          component={Select}
                          name={COMMISION_AGENCY_COUNTRY}
                          options={countriesOptions}
                          placeholder={taSelectCountryPlaceHolderLabel}
                          value={values[COMMISION_AGENCY_COUNTRY]}
                          isSearchable={true}
                          onChange={handleCountryChanges(values)}
                        />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <FormField
                          component={Select}
                          name={COMMISION_AGENCY_STATE}
                          isSearchable={true}
                          placeholder={taSelectStatePlaceHolderLabel}
                          options={stateOptionsCommission}
                          value={values[COMMISION_AGENCY_STATE]}
                        />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Row>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          placeholder={taCityPlaceHolderLabel}
                          name={COMMISION_AGENCY_CITY}
                          value={values[COMMISION_AGENCY_CITY]}
                        />
                      </Box>
                    </Col>
                    <Col span={12}>
                      <Box w="90%" pt={2}>
                        <InputField
                          placeholder={taPincodePlaceHolderLabel}
                          name={COMMISION_AGENCY_PINCODE}
                          value={values[COMMISION_AGENCY_PINCODE]}
                        />
                      </Box>
                    </Col>
                  </Row>
                  <VerticalSpacing size={1} />
                  <Box d="flex" justifyContent="flex-end">
                    <StyledButton type="secondary" onClick={handleReset}>
                      {taCancelButtonLabel}
                    </StyledButton>
                    <StyledButton
                      htmlType="submit"
                      onClick={() => {
                        setInvalidFileSize({
                          [AGENCY_CERTIFICATE_UPLOAD]: false,
                          [W_CERTIFICATE_UPLOAD]: false,
                        });
                      }}
                    >
                      {taSubmitButtonLabel}
                    </StyledButton>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </StyledContentBox>
      </StyledSection>
    </>
  );
};

TAEnrollmentPage.propTypes = propTypes;

export default withApplicationQuery(
  ENROLLMENT_PAGE_TRANSLATIONS_QUERY,
  withTheme(TAEnrollmentPage)
);
